/* By Beauty: circular navigation using the ORIGINAL cards, never cloned forms.
   Replace the previous bb-home.js completely, then reload the preview. */
(() => {
  if (window.bbCircularCarouselsInstalled) return;
  window.bbCircularCarouselsInstalled = true;
  const instances = new Map();

  class CircularCarousel {
    constructor(root, brands) {
      this.root = root;
      this.track = root.querySelector(brands ? '[data-bb-brand-track]' : '[data-bb-track]');
      if (!this.track) return;
      this.originals = Array.from(this.track.children);
      this.buttons = Array.from(root.querySelectorAll(brands ? '[data-bb-brand-direction]' : '[data-bb-direction]'));
      this.directionAttribute = brands ? 'data-bb-brand-direction' : 'data-bb-direction';
      this.progress = brands ? null : root.nextElementSibling?.matches('.bb-carousel__progress') ? root.nextElementSibling : null;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.track.classList.add('bb-loop-track');
      this.lastLeft = this.track.scrollLeft;
      this.direction = 0;
      this.busy = false;
      this.root.dataset.bbMoving = 'false';
      this.adjusting = false;
      this.touching = false;
      this.buttons.forEach(button => button.addEventListener('click', () => this.move(button.getAttribute(this.directionAttribute) === 'next' ? 1 : -1), opts));
      this.track.addEventListener('scroll', () => this.onScroll(), { ...opts, passive: true });
      this.track.addEventListener('pointerdown', () => { this.touching = true; }, { ...opts, passive: true });
      window.addEventListener('pointerup', () => { this.touching = false; this.scheduleSettle(); }, opts);
      window.addEventListener('pointercancel', () => { this.touching = false; this.scheduleSettle(); }, opts);
      this.track.addEventListener('keydown', event => {
        if (event.target.matches('input, textarea, select')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, opts);
      this.root.addEventListener('shopify:block:select', event => {
        const card = this.originals.find(item => item === event.target || item.contains(event.target));
        if (!card) return;
        this.track.scrollTo({ left: this.offset(card), behavior: 'instant' });
        this.direction = 0;
        this.updateProgress();
      }, opts);
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.track);
      this.measure();
    }
    offset(card) {
      return card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    }
    measure() {
      const cards = this.track.children;
      this.max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.pitch = cards.length > 1 ? this.offset(cards[1]) - this.offset(cards[0]) : 0;
      // Recycling requires one complete off-screen card. Otherwise retain native scrolling.
      this.loop = this.pitch > 0 && this.max >= this.pitch - 2 && getComputedStyle(this.track).direction !== 'rtl';
      if (!this.primed && this.loop && this.max >= 2 * this.pitch - 2 && this.track.scrollLeft < 2) {
        this.primed = true;
        this.recycle(-1);
      }
      this.buttons.forEach(b => { b.hidden = this.max <= 2; });
      if (this.progress) {
        const count = this.max > 2 ? Math.min(this.originals.length, 8) : 0;
        if (this.progress.children.length !== count) {
          this.progress.replaceChildren(...Array.from({ length: count }, () => document.createElement('i')));
        }
        this.progress.hidden = count === 0;
      }
      this.updateProgress();
    }
    updateProgress() {
      if (!this.progress?.children.length || !this.pitch) return;
      const cards = Array.from(this.track.children);
      const index = Math.min(cards.length - 1, Math.max(0, Math.round(this.track.scrollLeft / this.pitch)));
      const originalIndex = this.originals.indexOf(cards[index]);
      const bucket = Math.floor(originalIndex * this.progress.children.length / this.originals.length);
      Array.from(this.progress.children).forEach((dot, i) => dot.classList.toggle('is-active', i === bucket));
    }
    canMoveCard(card) {
      return card && !card.contains(document.activeElement) &&
        !card.querySelector('quick-add-modal[open], modal-dialog[open], dialog[open]');
    }
    recycle(direction) {
      if (!this.loop) return false;
      const card = direction > 0 ? this.track.firstElementChild : this.track.lastElementChild;
      if (!this.canMoveCard(card)) return false;
      const left = this.track.scrollLeft;
      // Only move a card that is already entirely outside the visible area.
      if (direction > 0 && left < this.pitch - 2) return false;
      if (direction < 0 && this.max - left < this.pitch - 2) return false;
      this.adjusting = true;
      this.track.classList.add('bb-loop-adjusting');
      if (direction > 0) this.track.append(card);
      else this.track.prepend(card);
      this.track.scrollTo({ left: left - direction * this.pitch, behavior: 'instant' });
      this.lastLeft = this.track.scrollLeft;
      cancelAnimationFrame(this.adjustFrame);
      this.adjustFrame = requestAnimationFrame(() => {
        this.track.classList.remove('bb-loop-adjusting');
        this.lastLeft = this.track.scrollLeft;
        this.adjusting = false;
        this.updateProgress();
      });
      return true;
    }
    move(direction) {
      if (this.busy || this.adjusting || this.max <= 2 || !this.pitch) return;
      clearTimeout(this.timer);
      this.direction = direction;
      const left = this.track.scrollLeft;
      // Prepare room BEFORE animating, so there is no visible return to the start.
      if ((direction > 0 && this.max - left < this.pitch - 2) || (direction < 0 && left < this.pitch - 2)) {
        this.recycle(direction);
      }
      this.busy = true;
      this.root.dataset.bbMoving = 'true';
      this.moveFrame = requestAnimationFrame(() => {
        const current = this.track.scrollLeft;
        const target = direction > 0
          ? (Math.floor((current + 2) / this.pitch) + 1) * this.pitch
          : (Math.ceil((current - 2) / this.pitch) - 1) * this.pitch;
        this.track.scrollTo({
          left: Math.max(0, Math.min(target, this.max)),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
        this.scheduleSettle();
      });
    }
    onScroll() {
      const left = this.track.scrollLeft;
      if (!this.adjusting && Math.abs(left - this.lastLeft) > 1) this.direction = left > this.lastLeft ? 1 : -1;
      this.lastLeft = left;
      this.updateProgress();
      if (!this.adjusting) this.scheduleSettle();
    }
    scheduleSettle() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (this.touching) return;
        this.busy = false;
        this.root.dataset.bbMoving = 'false';
        if (!this.adjusting && this.loop) {
          // After a native swipe settles, prepare space for the next swipe.
          const left = this.track.scrollLeft;
          if (this.direction > 0 && this.max - left < 2) this.recycle(1);
          else if (this.direction < 0 && left < 2) this.recycle(-1);
        }
        this.direction = 0;
        this.updateProgress();
      }, 180);
    }
    destroy() {
      this.controller.abort();
      this.observer.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.adjustFrame);
      cancelAnimationFrame(this.moveFrame);
      this.track.classList.remove('bb-loop-track', 'bb-loop-adjusting');
    }
  }
  function initialize(scope = document) {
    const selector = '[data-bb-carousel], [data-bb-brand-carousel]';
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new CircularCarousel(root, root.hasAttribute('data-bb-brand-carousel')));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (root === event.target || event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });
})();
